import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtUSD, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, statusTone } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader, KpiCard } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { Package, TrendingDown, Wallet } from "lucide-react";

const CATS = ["Equipment", "Building", "Vehicle", "Furniture", "Technology", "Land"];
const EMPTY = { asset_name: "", category: "Equipment", acquisition_date: "", cost: "", salvage_value: "", useful_life_years: "5", funding_source: "General Fund" };

export default function FixedAssets() {
  const { user } = useAuth();
  const canManage = ["admin", "finance_officer"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/fixed-assets").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/fixed-assets", { ...form, cost: Number(form.cost || 0), salvage_value: Number(form.salvage_value || 0), useful_life_years: Number(form.useful_life_years || 0) });
      toast.success("Asset added"); setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (!rows) return <Loader />;
  const cost = rows.reduce((s, r) => s + (r.cost || 0), 0);
  const accum = rows.reduce((s, r) => s + (r.accumulated_depreciation || 0), 0);
  const book = rows.reduce((s, r) => s + (r.book_value || 0), 0);

  const cols = [
    { key: "asset_name", label: "Asset", className: "font-medium" },
    { key: "category", label: "Category", render: (r) => <StatusBadge tone="blue">{r.category}</StatusBadge> },
    { key: "acquisition_date", label: "Acquired", render: (r) => fmtDate(r.acquisition_date) },
    { key: "cost", label: "Cost", align: "right", render: (r) => fmtUSD(r.cost) },
    { key: "annual_depreciation", label: "Annual Dep.", align: "right", render: (r) => fmtUSD(r.annual_depreciation) },
    { key: "accumulated_depreciation", label: "Accum. Dep.", align: "right", render: (r) => <span className="text-[#B45309]">{fmtUSD(r.accumulated_depreciation)}</span> },
    { key: "book_value", label: "Book Value", align: "right", render: (r) => <span className="font-semibold">{fmtUSD(r.book_value)}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge tone={statusTone(r.status)}>{r.status}</StatusBadge> },
  ];

  return (
    <div>
      <PageHeader title="Fixed Assets & Depreciation" subtitle="Capital asset register with straight-line depreciation and net book value."
        action={canManage && <AddButton onClick={() => setOpen(true)} label="Add Asset" testid="add-asset-btn" />} />
      <div className="grid grid-cols-3 gap-6 mb-6">
        <KpiCard label="Total Acquisition Cost" value={fmtUSD(cost)} icon={Package} />
        <KpiCard label="Accumulated Depreciation" value={fmtUSD(accum)} icon={TrendingDown} accent="#B45309" />
        <KpiCard label="Net Book Value" value={fmtUSD(book)} icon={Wallet} accent="#178070" />
      </div>
      <DataTable columns={cols} rows={rows} testid="assets-table" empty={{ title: "No assets recorded yet" }} />

      <Modal open={open} onOpenChange={setOpen} title="Add Fixed Asset" onSubmit={add} busy={busy} testid="asset-modal">
        <Field label="Asset Name" span={2}><TextInput required value={form.asset_name} onChange={(e) => setForm({ ...form, asset_name: e.target.value })} /></Field>
        <Field label="Category"><SelectInput value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} options={CATS} /></Field>
        <Field label="Acquisition Date"><TextInput type="date" required value={form.acquisition_date} onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })} /></Field>
        <Field label="Cost"><TextInput type="number" required value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></Field>
        <Field label="Salvage Value"><TextInput type="number" value={form.salvage_value} onChange={(e) => setForm({ ...form, salvage_value: e.target.value })} /></Field>
        <Field label="Useful Life (yrs)"><TextInput type="number" value={form.useful_life_years} onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })} /></Field>
        <Field label="Funding Source"><TextInput value={form.funding_source} onChange={(e) => setForm({ ...form, funding_source: e.target.value })} /></Field>
      </Modal>
    </div>
  );
}
